import React from "react";
import { motion } from "framer-motion";

const Projects = () => {
  // Paste your video embed URL here when the demo is live (YouTube/Loom/Vimeo embed link).
  // Example: https://www.youtube.com/embed/<id>
  const motivesEmbedUrl = "https://www.youtube.com/embed/hvipcRkN_u4";
  const treasureRunnerEmbedUrl = "https://www.youtube.com/embed/epjoNObH2kE";

  const motivesConceptGroups = [
    {
      title: "Geospatial Ranking Engine",
      items: [
        "All ranking happens in a single SQL function (search_and_rank_places) using a 3-CTE pipeline that resolves user state, filters by radius, and computes composite scores before anything hits the client.",
        "Uses PostGIS geography type instead of geometry so distance calculations account for Earth's curvature, giving meter-accurate results across Canada without any client-side correction.",
        "Review scores are log-weighted with ln(review_count + 1) / ln(1000), so a 4.7★ place with 800 reviews ranks above a 4.9★ with 6. Stops low-sample spots from floating to the top.",
        "A GIST spatial index paired with a partial composite index on (city_id, pillar, rating DESC) lets the query planner handle filtering and sorting in one pass with no extra sort step.",
      ],
    },
    {
      title: "Data Architecture",
      items: [
        "city_name is stored directly on the places table to avoid a JOIN on every list query. city_id is still there for referential integrity in the seeding pipeline.",
        "Wishlists and visit history share one table with nullable saved_at and visited_at columns. Moving a place from wishlist to visited is a single atomic update with no cross-table race conditions.",
        "Open/closed status for overnight hours checks both the current and previous day's periods, so a bar open 10pm to 3am shows correctly at 1am. The SQL and JS logic are kept in sync intentionally.",
        "A missing subscriptions row means free tier. No nullable status column, no default to misconfigure. The schema makes the wrong state structurally impossible.",
      ],
    },
    {
      title: "Security Boundary Design",
      items: [
        "Row Level Security is the only security layer. The Supabase anon key is public by design, but RLS policies make it impossible to read another user's saved places, preferences, or subscription data regardless of the query.",
        "The Google Places API key never touches the client bundle. The seeding pipeline is server-side only, and the app resolves coordinates to city names through a PostGIS RPC at runtime, so a client-side Places API call is structurally impossible.",
        "search_and_rank_places is declared as a STABLE SQL function so Postgres can cache results within a transaction and the query planner can inline it, eliminating a whole class of side-effect bugs.",
      ],
    },
    {
      title: "Client State Architecture",
      items: [
        "Auth state lives in a Zustand store initialized once in the root layout. No React Context Provider means no re-render cascades when the session changes.",
        "Discovery filters are kept in memory and automatically reset after 10 minutes in the background using React Native's AppState API. Filters persist across tab switches but never carry stale location data into the next session.",
        "TanStack Query cache keys are rounded to 100m coordinate precision so users moving within a city block hit the cache. A neighbourhood-level move triggers a fresh fetch, and useInvalidateDiscovery() flushes all active filter combinations after any save or visit.",
      ],
    },
  ];

  const motivesHighlights = [
    "React Native / Expo",
    "TypeScript Strict",
    "PostGIS Geospatial",
    "TanStack Query",
    "Supabase / PostgreSQL",
    "Zustand",
    "Row Level Security",
    "Expo Router",
  ];

  const treasureRunnerConceptGroups = [
    {
      title: "Systems Core (C)",
      items: [
        "Game engine built in C as a shared library (libbackend.so)",
        "Manual memory management with clear ownership semantics",
        "Opaque C types to enforce encapsulation through APIs",
        "Unified status-code error handling across modules",
      ],
    },
    {
      title: "Custom Graph ADT",
      items: [
        "Generic directed graph implemented from scratch",
        "Cycle detection, reachability, connectivity checks",
        "In/out-degree analysis for room connectivity",
        "Function-pointer based generic payload support",
      ],
    },
    {
      title: "Python + C Bridge",
      items: [
        "ctypes bindings from Python into the C shared library",
        "Mirrored enums/structs for safer interop",
        "C-allocated strings released from Python safely",
      ],
    },
    {
      title: "Architecture & Gameplay",
      items: [
        "Clean MVC layering in Python (bindings/models/ui)",
        "Curses-based terminal UI with ASCII rendering",
        "Procedural dungeon generation from .ini configs",
        "Seeded randomness for reproducible level generation",
        "Player persistence in JSON across sessions",
      ],
    },
  ];

  const treasureRunnerHighlights = [
    "C Systems Programming",
    "Data Structures & Algorithms",
    "ctypes Interoperability",
    "MVC Architecture",
    "Procedural Generation",
    "Memory Management",
    "Terminal UI (curses)",
    "JSON Persistence",
  ];

  return (
    <section className="projects-page">
      <motion.h3
        initial={{ y: 20, opacity: 0 }}
        whileInView={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        Projects
      </motion.h3>

      {/* Motives — Featured Project */}
      <motion.div
        className="project-feature"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="project-feature-header">
          <div>
            <p className="project-kicker">Featured Project</p>
            <h4 className="project-title">Motives</h4>
          </div>
          <span className="project-badge">Mobile Discovery Platform</span>
        </div>

        <p className="project-summary">
          Motives is a Canada-first mobile discovery platform that helps groups
          find restaurants and activities they'll actually agree on. Built solo
          from the ground up, with a 6-stage data seeding pipeline, a
          PostGIS-powered ranking engine, a TypeScript-strict React Native
          codebase, 25+ database migrations, and a live Supabase backend.
        </p>

        <div className="project-media-wrap">
          {motivesEmbedUrl ? (
            <iframe
              className="project-video"
              src={motivesEmbedUrl}
              title="Motives Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="project-video-placeholder">
              Demo coming soon — paste your YouTube embed URL into{" "}
              <code>motivesEmbedUrl</code> in <code>Projects.js</code> to show
              it here.
            </div>
          )}
        </div>

        <div className="project-highlights">
          {motivesHighlights.map((item) => (
            <span key={item} className="project-pill">
              {item}
            </span>
          ))}
        </div>

        <div className="project-concepts-grid">
          {motivesConceptGroups.map((group) => (
            <article key={group.title} className="project-concept-card">
              <h5>{group.title}</h5>
              <ul>
                {group.items.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </motion.div>

      {/* Treasure Runner */}
      <motion.div
        className="project-feature"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="project-feature-header">
          <div>
            <p className="project-kicker">Featured Project</p>
            <h4 className="project-title">Treasure Runner</h4>
          </div>
          <span className="project-badge">Terminal Puzzle Game</span>
        </div>

        <p className="project-summary">
          Treasure Runner is a multi-room terminal puzzle game where players
          navigate ASCII-rendered dungeons, collect treasures, push boulders
          onto switches, unlock portals, and transition between rooms.
        </p>

        <div className="project-media-wrap">
          {treasureRunnerEmbedUrl ? (
            <iframe
              className="project-video"
              src={treasureRunnerEmbedUrl}
              title="Treasure Runner Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="project-video-placeholder">
              Add your demo embed URL in <code>Projects.js</code> (
              <code>treasureRunnerEmbedUrl</code>) to show the playable video
              here.
            </div>
          )}
        </div>

        <div className="project-highlights">
          {treasureRunnerHighlights.map((item) => (
            <span key={item} className="project-pill">
              {item}
            </span>
          ))}
        </div>

        <div className="project-concepts-grid">
          {treasureRunnerConceptGroups.map((group) => (
            <article key={group.title} className="project-concept-card">
              <h5>{group.title}</h5>
              <ul>
                {group.items.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Projects;
